"use client";

import Link from "next/link";
import { storyblokEditable } from "@storyblok/react/rsc";
import { useNow } from "@/lib/hooks/useNow";
import { parseBerlinDate } from "@/lib/utils/berlinDate";

import { Button } from "@/components/ui/button";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { cn } from "@/lib/utils";
import { RichTextField } from "@/components/typography/RichTextField";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import type { MultilinkStoryblok } from "@/component-types-sb";

type StepsItemStoryblok = {
  title: string;
  text?: string;
  cta_label?: string;
  cta_label_inactive?: string;
  cta_link?: MultilinkStoryblok;
  active_from?: string;
  active_until?: string;
  force_active?: boolean;
  _uid: string;
  component: "steps_item";
  [k: string]: any;
};

type StepsItemProps = {
  blok: StepsItemStoryblok;
};

type StepState = "future" | "active" | "past";

function getStepState(blok: StepsItemStoryblok, now: Date | null): StepState {
  // Manual override
  if (blok.force_active) return "active";

  // Before hydration, default to "future" to match server render
  if (!now) return "future";

  const from = blok.active_from
    ? parseBerlinDate(blok.active_from)
    : null;
  const until = blok.active_until
    ? parseBerlinDate(blok.active_until)
    : null;

  // No dates set → future (default inactive)
  if (!from && !until) return "future";

  // Has from date but hasn't started yet
  if (from && now < from) return "future";

  // Has until date and has passed
  if (until && now > until) return "past";

  // Within the active range (from <= now <= until)
  // Or only from is set and we're past it
  if (from && now >= from) return "active";

  return "future";
}

const StepsItem = ({ blok }: StepsItemProps) => {
  const now = useNow();
  const state = getStepState(blok, now);
  const isActive = state === "active";
  const isPast = state === "past";

  const ctaHref = getUrlFromLinkObject(blok.cta_link);
  const hasCta = blok.cta_label && ctaHref;
  const ctaLabel =
    isPast && blok.cta_label_inactive
      ? blok.cta_label_inactive
      : blok.cta_label;

  return (
    <div
      className={cn(
        "flex flex-col gap-3 px-3 md:px-6 py-2 md:py-4 rounded-2xl",
        isActive && "bg-sun-100",
      )}
      {...storyblokEditable(blok)}
    >
      {/* Title + Text */}
      <div className={cn("flex flex-col", isPast && "opacity-40")}>
        <span className="text-copy-bold">{blok.title}</span>
        {blok.text && <RichTextField value={blok.text} className="text-copy" />}
      </div>

      {/* CTA: shown when active or past */}
      {hasCta && (isActive || isPast) && (
        <div className={cn(isPast && "opacity-30")}>
          <Button variant="black" size="sm" asChild={!isPast} disabled={isPast}>
            {isPast ? (
              <>{ctaLabel}</>
            ) : (
              <Link href={ctaHref}>
                {ctaLabel}
                <ArrowRight size="sm" />
              </Link>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default StepsItem;
